import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { countries } from '../mock';
import { toast } from 'sonner';
import { submitLead, fetchGeoLocation } from '../services/api';

const RegisterPopup = ({ onClose, packageInfo = null }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    countryCode: '+1',
    country: 'US',
    phone: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [geoLoading, setGeoLoading] = useState(true);

  // Fetch geolocation on component mount
  useEffect(() => {
    const loadGeoLocation = async () => {
      try {
        const geoData = await fetchGeoLocation();
        if (geoData?.data) {
          // Find matching country in our list
          const matchedCountry = countries.find(
            c => c.countryCode === geoData.data.countryCode
          );
          
          if (matchedCountry) {
            setFormData(prev => ({
              ...prev,
              countryCode: matchedCountry.code,
              country: matchedCountry.countryCode
            }));
          }
        }
      } catch (error) {
        console.error('Error loading geolocation:', error);
      } finally {
        setGeoLoading(false);
      }
    };

    loadGeoLocation();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate phone number
    if (formData.phone.length < 7) {
      toast.error('Please enter a valid phone number');
      return;
    }
    
    setIsSubmitting(true);

    try {
      const result = await submitLead(formData, packageInfo);

      if (result.success) {
        toast.success(result.message || 'Thank you! Redirecting...');
        
        // Redirect to application page with customer ID
        setTimeout(() => {
          window.location.href = `/application/main?customerid=${result.customerId}`;
        }, 1000);
      } else {
        toast.error(result.error || 'Something went wrong. Please try again.');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error('Failed to submit form. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Phone number validation: only allow digits
    if (name === 'phone') {
      const numericValue = value.replace(/\D/g, ''); // Remove non-digits
      setFormData({
        ...formData,
        [name]: numericValue
      });
    } else {
      setFormData({
        ...formData,
        [name]: value
      });
    }
  };

  const handleCountryChange = (value) => {
    const selectedCountry = countries.find(c => c.code === value);
    setFormData({
      ...formData,
      countryCode: value,
      country: selectedCountry?.countryCode || 'US'
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-md w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-gray-900">Get Started Today</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          {packageInfo && (
            <div className="bg-brand-50 border border-brand-200 rounded-lg p-3 mb-4">
              <p className="text-sm text-brand-800">
                <strong>Selected Package:</strong> {packageInfo.packageTitle} - ${packageInfo.packageAmount}
              </p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Hidden fields for IP info */}
            <input type="hidden" id="hiddenId" name="hiddenId" />
            <input type="hidden" className="IpInfo" name="ipInfo" />
            <input type="hidden" className="locerr" name="locerr" />
            
            <div>
              <Label htmlFor="name">Full Name *</Label>
              <Input
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="Enter your full name"
                required
                className="mt-1"
              />
            </div>

            <div>
              <Label htmlFor="email">Email Address *</Label>
              <Input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="your@email.com"
                required
                className="mt-1"
              />
            </div>

            <div>
              <Label>Contact Number *</Label>
              <div className="flex gap-2 mt-1">
                <Select
                  value={formData.countryCode}
                  onValueChange={handleCountryChange}
                  disabled={geoLoading}
                >
                  <SelectTrigger className="w-32">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {countries.map((country) => (
                      <SelectItem key={country.countryCode} value={country.code}>
                        {country.code} {country.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Input
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="Phone number"
                  required
                  className="flex-1"
                />
              </div>
              {geoLoading && (
                <p className="text-xs text-gray-500 mt-1">Loading country...</p>
              )}
            </div>

            <div>
              <Label htmlFor="message">Message</Label>
              <Textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                placeholder="Tell us about your trademark needs..."
                rows={4}
                className="mt-1"
              />
            </div>

            <Button 
              type="submit" 
              className="w-full bg-brand-500 hover:bg-brand-600 text-white"
              disabled={isSubmitting || geoLoading}
            >
              {isSubmitting ? 'Submitting...' : 'Submit Request'}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default RegisterPopup;